import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BenefitType } from 'edenred-ui';

import { FormFields } from '@epi-forms/helpers';
import {
  FundsLoadingTypes,
  Payment,
  Schedule
} from '@epi-constants/benefitTypes';
import { getNumber } from '@epi-helpers/numeral';
import { getBenefitSelected } from '@epi-selectors/benefitGroupSettings';
import { IUpdateFooter } from '@epi-models/pages/TransferPage';
import { isTomorrow } from '@epi-helpers/TopupHelper';
import { isEmptyObj } from '@epi-helpers/isEmptyObj';
import { useOnboardingPayment, useOnboardingWizard } from '@epi-shared/hooks';
import {
  getOnboardingIsSecondStep,
  getOnboardingLoadBenefitPageType
} from '@epi-store/onboarding/onboarding.selectors';
import { TopupFormStatusesType } from '@epi-constants/topupForm';

import {
  useTopupEmployees,
  useTopupPendingBenefits,
  useTopupRequest,
  useTopupSwitch
} from '../../../hooks';

import { TopupInProgress } from './TopupInProgress';
import { TopupConfirmationModal } from './TopupConfirmationModal';
import { MyChoiceInfo } from './MyChoiceInfo';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';
import { BlockedDirectLoadInfo } from './BlockedDirectLoadInfo';
import {
  BenefitSelection,
  LoadAmount,
  PaymentMethodStep,
  ScheduleLoad,
  LoadEmployeesForm,
  TopupFormNotifications
} from './TopupFormSteps';

export const TopupForm = ({
  updateFooter,
  form,
  loadableBenefits
}: IUpdateFooter) => {
  const { t } = useTranslation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showTopupForm, setShowTopupForm] = useState(true);
  const [topupRequestStatus, setTopupRequestStatus] =
    useState<TopupFormStatusesType | null>(null);

  const language = useSelector(getApplicationLanguageId);

  const {
    watch,
    handleSubmit,
    setValue,
    resetField,
    getValues,
    reset,
    formState: { isValid, errors }
  } = form;

  const benefitType = watch(FormFields.Benefit);
  const benefitGroupIds = watch(FormFields.BenefitGroupIds);
  const topupPerPerson = watch(FormFields.MoneyAmount);
  const scheduleType = watch(FormFields.ScheduleType);
  const paymentMethod = watch(FormFields.PaymentMethod);
  const eInvoiceAddress = watch(FormFields.EInvoiceAddress);
  const operator = watch(FormFields.EInvoiceOperator);
  const daysToTopup = watch(FormFields.DaysForLunari);
  const benefitSettings = useSelector(state =>
    getBenefitSelected(state, benefitType)
  );

  const {
    paginationCount,
    toggleEmployee,
    checkedCount,
    lowestYearlyLimit,
    topupBeneficiariesIds,
    toggleAllEmployee,
    isAllEmployeeChecked,
    setForceUpdate,
    formattedTopup,
    formattedFee,
    formattedVat,
    formattedTopupWithFees,
    formattedRemainingBalance,
    remainingBalance,
    fee,
    vat,
    formattedMissingAmount,
    daysExceed,
    employeesToTopup,
    checkableEmployees
  } = useTopupEmployees();

  const { handleToggler, isToggle, showOnHoldBtn } = useTopupSwitch();

  const { isOnBoardingActive, flags } = useOnboardingWizard();
  const onboardingPayment = useOnboardingPayment(reset);
  const isSecondStep = useSelector(getOnboardingIsSecondStep);
  const onboardingPageType = useSelector(getOnboardingLoadBenefitPageType);

  const onboardingBackButton = isOnBoardingActive
    ? {
        backText: t('components.onboarding.buttons.back'),
        onBack: onboardingPayment?.onboardingLoadBenefit?.onBack
          ? () => {
              if (onboardingPayment?.onboardingLoadBenefit?.onBack) {
                onboardingPayment?.onboardingLoadBenefit?.onBack();
              }
            }
          : undefined
      }
    : {};

  const onboardingCancelButton = isOnBoardingActive
    ? {
        cancelText: t('components.onboarding.buttons.skip'),
        onCancel: onboardingPayment?.onboardingLoadBenefit?.onSkip
          ? () => {
              if (onboardingPayment?.onboardingLoadBenefit?.onSkip) {
                onboardingPayment?.onboardingLoadBenefit.onSkip();
              }
            }
          : undefined,
        disableCancel:
          isSecondStep && !flags?.isMoneyTransfered && !flags?.isLoadInitiated
      }
    : {};

  const { postTopupRequest, isTopupPending, isTopupProcessing, summaryId } =
    useTopupRequest(
      setShowConfirmationModal,
      isToggle,
      showOnHoldBtn,
      setTopupRequestStatus
    );

  const { isPendingBenefit, getPendingBenefits } = useTopupPendingBenefits(
    topupBeneficiariesIds
  );

  const isPaymentTypeTopUpRequest = checkableEmployees.some(
    ({ fundsLoadingType }) =>
      fundsLoadingType === FundsLoadingTypes.TopUpRequest
  );

  const isPaymentTypeDirectLoad = checkableEmployees.some(
    ({ fundsLoadingType }) => fundsLoadingType === FundsLoadingTypes.DirectLoad
  );

  const isLunchTopupRequest =
    isPaymentTypeTopUpRequest && benefitType === BenefitType.lunch;

  const isLunchDirectLoad =
    isPaymentTypeDirectLoad && benefitType === BenefitType.lunch;

  const onSubmit = () => {
    if (scheduleType === Schedule.SelectDate) {
      const benefit = getValues(FormFields.Benefit);
      getPendingBenefits(benefit);
    }
    setShowConfirmationModal(true);
  };
  const handleCloseModal = () => setShowConfirmationModal(false);

  const handleTopupRequest = () => {
    const payload = {
      amount: topupPerPerson ? (getNumber(topupPerPerson) * 100).toFixed(0) : 0,
      numberOfWorkingDays: isLunchDirectLoad
        ? Number(getValues(FormFields.DaysForLunari))
        : 0,
      benefitType,
      beneficiariesIds: topupBeneficiariesIds,
      paymentMethod,
      eInvoiceAddress,
      operator
    };
    postTopupRequest(payload);
    resetField(FormFields.MoneyAmount);
    resetField(FormFields.Benefit);
    resetField(FormFields.BenefitGroupIds);
  };

  const handleResetTopupStatus = () => {
    setTopupRequestStatus(null);
  };

  useEffect(() => {
    if (loadableBenefits?.length === 1) {
      setValue(FormFields.Benefit, loadableBenefits[0]);
    }
  }, [summaryId, showTopupForm, isSecondStep]);

  useEffect(() => {
    if (isTopupProcessing && !isOnBoardingActive) {
      setShowTopupForm(false);
    }
  }, [isTopupProcessing]);

  useEffect(() => {
    if (showTopupForm) {
      updateFooter({
        submitText: t('containers.deposit_money.topup_tab.footer_button'),
        onSubmit: handleSubmit(onSubmit),
        hidden: isLunchTopupRequest,
        disableSubmit:
          !isValid ||
          !isEmptyObj(errors) ||
          (remainingBalance < 0 && paymentMethod === Payment.Deduction) ||
          !paymentMethod ||
          !topupBeneficiariesIds.length ||
          !checkedCount ||
          (scheduleType === Schedule.SelectDate &&
            !isTomorrow(watch(FormFields.ScheduleDate))) ||
          (benefitType === BenefitType.lunch &&
            (!daysToTopup || daysToTopup === 0)),
        ...onboardingBackButton,
        ...onboardingCancelButton
      });
    }
    if (!showTopupForm) {
      updateFooter(undefined);
    }
  }, [
    showTopupForm,
    isValid,
    remainingBalance,
    topupBeneficiariesIds.length,
    isEmptyObj(errors),
    paymentMethod,
    scheduleType,
    benefitType,
    employeesToTopup,
    language,
    daysToTopup,
    onboardingPageType,
    isSecondStep,
    isLunchTopupRequest,
    benefitGroupIds
  ]);

  if (!showTopupForm && !isOnBoardingActive) {
    return (
      <TopupInProgress
        setShowTopupForm={setShowTopupForm}
        isTopupProcessing={isTopupProcessing}
        summaryId={summaryId}
        loadableBenefits={loadableBenefits}
        setForceUpdate={setForceUpdate}
      />
    );
  }

  return (
    <>
      <BlockedDirectLoadInfo blockDirectLoad={isLunchTopupRequest} />
      <MyChoiceInfo loadableBenefits={loadableBenefits} />
      <BenefitSelection
        loadableBenefits={loadableBenefits}
        onboardingPageType={onboardingPageType}
      />
      <ScheduleLoad isLunchTopupRequest={isLunchTopupRequest} />

      <LoadAmount
        daysExceed={daysExceed}
        daysToTopup={daysToTopup}
        remainingBalance={remainingBalance}
        benefitType={benefitType}
        lowestYearlyLimit={lowestYearlyLimit}
        benefitSettings={benefitSettings}
      />
      <LoadEmployeesForm
        isLunchTopupRequest={isLunchTopupRequest}
        formattedTopup={formattedTopup}
        formattedVat={formattedVat}
        paginationCount={paginationCount}
        toggleEmployee={toggleEmployee}
        toggleAllEmployee={toggleAllEmployee}
        checkedCount={checkedCount}
        isAllEmployeeChecked={isAllEmployeeChecked}
        formattedFee={formattedFee}
        formattedTopupWithFees={formattedTopupWithFees}
        formattedRemainingBalance={formattedRemainingBalance}
        remainingBalance={remainingBalance}
        fee={fee}
        vat={vat}
        handleToggler={handleToggler}
        isToggle={isToggle}
        showOnHoldBtn={showOnHoldBtn}
      />
      <PaymentMethodStep
        isLunchTopupRequest={isLunchTopupRequest}
        remainingBalance={remainingBalance}
        formattedMissingAmount={formattedMissingAmount}
      />
      <TopupFormNotifications
        isLunchTopupRequest={isLunchTopupRequest}
        benefitType={benefitType}
      />
      <TopupConfirmationModal
        showModal={showConfirmationModal}
        employeesCount={checkedCount}
        handleCloseModal={handleCloseModal}
        handleTopupRequest={handleTopupRequest}
        totalAmount={formattedTopupWithFees}
        isTopupPending={isTopupPending}
        showOnHoldText={isToggle}
        topupAmount={formattedTopup}
        isPendingBenefit={isPendingBenefit}
        showOnboardingContent={
          isOnBoardingActive &&
          (!showTopupForm || isTopupPending || !!topupRequestStatus)
        }
        topupRequestStatus={topupRequestStatus}
        resetTopupStatus={handleResetTopupStatus}
      />
    </>
  );
};

TopupForm.displayName = 'TopupForm';
